/**
 * Phase 4 slice 4 — Inspection Request detail. Set per-item pass/fail/na +
 * remarks, then complete the inspection (a "failed" result auto-raises an NCR).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const ITEM_OPTS = [
  { v: 'pass', label: 'Pass', tone: 'bg-emerald-600 text-white border-emerald-600' },
  { v: 'fail', label: 'Fail', tone: 'bg-red-600 text-white border-red-600' },
  { v: 'na', label: 'N/A', tone: 'bg-slate-400 text-white border-slate-400' },
];
import { formatDateTime as fmt } from '../../lib/format';
import { GRNInspectDialog } from '../../components/stores/GRNInspectDialog';

const Field = ({ label, value }) => (
  <div><div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</div><div className="text-sm text-slate-800">{value || '—'}</div></div>
);

export default function InspectionRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [items, setItems] = useState([]);
  const [obs, setObs] = useState('');
  const [busy, setBusy] = useState(false);
  const [inspectGrn, setInspectGrn] = useState(false);

  const load = () => apiGet(`/quality/inspection-requests/${id}`).then((d) => {
    setDoc(d); setItems(d.checklist_items || []); setObs(d.observations || '');
  }).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const setItemStatus = (itemId, st) => setItems(items.map((it) => it.id === itemId ? { ...it, status: st } : it));
  const setItemRemark = (itemId, r) => setItems(items.map((it) => it.id === itemId ? { ...it, remarks: r } : it));

  const saveItems = async () => {
    setBusy(true);
    try {
      const item_results = {};
      items.forEach((it) => { item_results[it.id] = { status: it.status, remarks: it.remarks }; });
      await apiPut(`/quality/inspection-requests/${id}`, { item_results, observations: obs });
      toast.success('Checklist saved');
      load();
    } catch (e) { toast.error(e?.response?.status === 403 ? 'No permission' : 'Failed to save'); } finally { setBusy(false); }
  };

  const complete = async (status) => {
    setBusy(true);
    try {
      const r = await apiPost(`/quality/inspection-requests/${id}/complete`, { status, observations: obs });
      toast.success(status === 'failed' ? 'Completed — NCR auto-raised' : 'Inspection completed');
      if (r.linked_ncr_id) navigate(`/app/quality/ncrs/${r.linked_ncr_id}`); else load();
    } catch (e) { toast.error(e?.response?.status === 403 ? 'No permission' : 'Failed'); } finally { setBusy(false); }
  };

  if (!doc) return <div className="text-center text-xs text-slate-400 py-16 uppercase tracking-widest font-display">Loading…</div>;
  const statusLocked = ['passed', 'passed_with_observations', 'failed', 'cancelled'].includes(doc.status);
  const locked = statusLocked || doc.source === 'grn';
  const anyFail = items.some((i) => i.status === 'fail');

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate('/app/quality/inspection-requests')} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-3" data-testid="insp-back"><ArrowLeft size={14} /> Back to Inspections</button>
      <PageHeader overline={`${doc.stage} · ${doc.inspector_role}`} title={doc.activity || 'Inspection Request'}
        actions={<span className="tag border bg-slate-100 text-slate-700 border-slate-200" data-testid="insp-status">{(doc.status || '').replace(/_/g, ' ')}</span>} />

      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="inspection-detail">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Field label="Raised By" value={doc.raised_by} />
          <Field label="Requested Inspector" value={doc.requested_inspector} />
          <Field label="Scheduled" value={fmt(doc.scheduled_at)} />
          <Field label="Completed" value={doc.completed_at ? `${doc.completed_by} · ${fmt(doc.completed_at)}` : '—'} />
        </div>

        {doc.source === 'grn' && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-800 flex items-center justify-between gap-3" data-testid="insp-grn-banner">
            <span>Material-arrival inspection for <b>{doc.source_grn_no}</b> — record it on the GRN so approved quantity is released to stock.</span>
            {!statusLocked && <button onClick={() => setInspectGrn(true)} className="shrink-0 px-3 py-1.5 text-xs font-display font-bold rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="insp-inspect-grn-btn">Inspect GRN</button>}
          </div>
        )}
        {inspectGrn && doc.source_grn_id && <GRNInspectDialog grnId={doc.source_grn_id} onClose={() => setInspectGrn(false)} onInspected={load} />}

        {doc.linked_ncr_id && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-700 flex items-center justify-between">
            <span>This inspection failed — an NCR was raised.</span>
            <Link to={`/app/quality/ncrs/${doc.linked_ncr_id}`} className="font-display font-bold underline" data-testid="insp-linked-ncr">View NCR →</Link>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Checklist</div>
          <div className="space-y-2">
            {items.length === 0 && <div className="text-xs text-slate-400">No checklist items.</div>}
            {items.map((it) => (
              <div key={it.id} className="border border-slate-200 rounded-sm p-3" data-testid={`insp-item-${it.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-sm font-medium text-slate-800">{it.item}</span>
                  {!locked && (
                    <div className="flex gap-1">
                      {ITEM_OPTS.map((o) => (
                        <button key={o.v} onClick={() => setItemStatus(it.id, o.v)}
                          className={`px-2.5 py-1 rounded-sm text-[11px] font-bold border transition-colors ${it.status === o.v ? o.tone : 'bg-white text-slate-500 border-slate-300'}`}
                          data-testid={`insp-item-${it.id}-${o.v}`}>{o.label}</button>
                      ))}
                    </div>
                  )}
                  {locked && <span className={`tag border ${it.status === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : it.status === 'fail' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>{it.status}</span>}
                </div>
                {!locked && <input value={it.remarks || ''} onChange={(e) => setItemRemark(it.id, e.target.value)} placeholder="Remarks" className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs" />}
                {locked && it.remarks && <div className="text-xs text-slate-500">{it.remarks}</div>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Observations</div>
          <textarea value={obs} onChange={(e) => setObs(e.target.value)} disabled={locked} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50" data-testid="insp-observations" />
        </div>

        {!locked && doc.source !== 'grn' && (
          <div className="border-t border-slate-200 pt-4 flex flex-wrap items-center gap-2">
            <button onClick={saveItems} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-display font-bold rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60" data-testid="insp-save-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : 'Save Checklist'}</button>
            <div className="flex-1" />
            <button onClick={() => complete(anyFail ? 'failed' : 'passed')} disabled={busy} className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold rounded-sm text-white disabled:opacity-60 ${anyFail ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid="insp-complete-btn">
              <CheckCircle2 size={14} /> Complete as {anyFail ? 'Failed' : 'Passed'}
            </button>
            {!anyFail && <button onClick={() => complete('passed_with_observations')} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-display font-bold rounded-sm border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-60" data-testid="insp-complete-obs-btn">Pass w/ Obs.</button>}
          </div>
        )}
      </div>
    </div>
  );
}
